import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';
import FormPresupuesto from './FormPresupuesto';
import DetallePresupuesto from './DetallePresupuesto';

const ESTADO_BADGE = {
  borrador:  { label: 'Borrador',  bg: '#1A1F1D', color: '#8A9994' },
  enviado:   { label: 'Enviado',   bg: '#0D1E2E', color: '#60A5FA' },
  aceptado:  { label: 'Aceptado', bg: '#12382D', color: '#4CAF50' },
  rechazado: { label: 'Rechazado', bg: '#2D0F0F', color: '#EF4444' },
};

const TIPO_LABEL = {
  reparacion:    'Reparación',
  modernizacion: 'Modernización',
  normativo:     'Normativo',
};

const CLP = (n) =>
  Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

// Normaliza para buscar sin distinguir tildes ni mayúsculas
const normalizar = (t) =>
  String(t || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim();

const SELECT_ST = {
  padding: '8px 10px', border: '1.5px solid #2E4A40', borderRadius: 8,
  fontSize: 14, background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
};

export default function ListaPresupuestos() {
  const { tecnico } = useAuth();
  const esAdmin = ['admin', 'superadmin'].includes(tecnico?.rol);
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('vigentes');
  const [busqueda, setBusqueda] = useState('');
  const [descargandoId, setDescargandoId] = useState(null);
  const [vista, setVista] = useState('lista');
  const [seleccionado, setSeleccionado] = useState(null);
  const [error, setError] = useState('');
  const [menuEstado, setMenuEstado] = useState(null);
  const [expandido, setExpandido] = useState(null);   // id del presupuesto con ítems desplegados
  const [itemsCache, setItemsCache] = useState({});    // { [id]: { loading, items, error } }

  const toggleExpandir = async (id) => {
    if (expandido === id) { setExpandido(null); return; }
    setExpandido(id);
    if (!itemsCache[id]) {
      setItemsCache(prev => ({ ...prev, [id]: { loading: true, items: null, error: '' } }));
      try {
        const r = await api.get(`/presupuestos/${id}`);
        setItemsCache(prev => ({ ...prev, [id]: { loading: false, items: r.data.items || [], error: '' } }));
      } catch (e) {
        setItemsCache(prev => ({ ...prev, [id]: { loading: false, items: null, error: e.response?.data?.error || 'Error al cargar ítems' } }));
      }
    }
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filtroTipo)   params.tipo   = filtroTipo;
      if (filtroEstado && filtroEstado !== 'todos') params.estado = filtroEstado;
      const r = await api.get('/presupuestos', { params });
      setPresupuestos(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = useMemo(() => {
    const q = normalizar(busqueda);
    if (!q) return presupuestos;
    return presupuestos.filter(p => normalizar(p.comunidad).includes(q));
  }, [presupuestos, busqueda]);

  // Descarga el PDF sin abrir el detalle: pide el blob al backend y lo baja
  const descargarPDF = async (p) => {
    setDescargandoId(p.id);
    try {
      const token = localStorage.getItem('solvert_token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/presupuestos/${p.id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Error al generar PDF');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${p.numero || 'presupuesto'}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 150);
    } catch (e) {
      alert(e.message || 'Error al descargar PDF');
    } finally {
      setDescargandoId(null);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto en borrador?')) return;
    try {
      await api.delete(`/presupuestos/${id}`);
      cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const TRANSICIONES = {
    borrador:  ['enviado'],
    enviado:   ['aceptado', 'rechazado'],
    rechazado: ['borrador'],
  };

  const ESTADO_LABEL_BTN = {
    enviado:   'Enviado',
    aceptado:  'Aceptado',
    rechazado: 'Rechazado',
    borrador:  'Borrador',
  };

  const abrirMenuEstado = (e, id, estadoActual) => {
    const opciones = TRANSICIONES[estadoActual] || [];
    if (!opciones.length) return;
    setMenuEstado({ id, opciones, rect: e.currentTarget.getBoundingClientRect() });
  };

  const abrirEditar = (p) => {
    if (p.estado === 'enviado') {
      const ok = window.confirm(
        'Este presupuesto ya fue ENVIADO al cliente.\n\n' +
        'Al editarlo se actualizarán los datos en el sistema, pero el PDF y el correo ' +
        'NO se reenvían automáticamente. ¿Continuar?'
      );
      if (!ok) return;
    }
    setSeleccionado(p.id);
    setVista('editar');
  };

  const aplicarEstado = async (id, nuevo) => {
    setMenuEstado(null);
    try {
      await api.put(`/presupuestos/${id}/estado`, { estado: nuevo });
      cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al cambiar estado');
    }
  };

  if (vista === 'nuevo') {
    return (
      <FormPresupuesto
        onGuardado={() => { setVista('lista'); cargar(); }}
        onCancelar={() => setVista('lista')}
      />
    );
  }

  if (vista === 'editar' && seleccionado) {
    return (
      <FormPresupuesto
        id={seleccionado}
        onGuardado={() => { setVista('lista'); cargar(); }}
        onCancelar={() => setVista('lista')}
      />
    );
  }

  if (vista === 'detalle' && seleccionado) {
    return (
      <DetallePresupuesto
        id={seleccionado}
        onVolver={() => { setVista('lista'); cargar(); }}
      />
    );
  }

  return (
    <div>
      {/* Menú flotante de cambio de estado */}
      {menuEstado && (
        <>
          <div onClick={() => setMenuEstado(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 999 }} />
          <div style={{
            position: 'fixed',
            top:  menuEstado.rect.bottom + 4,
            left: menuEstado.rect.left,
            zIndex: 1000,
            background: '#1A1F1D',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            minWidth: 140,
            overflow: 'hidden',
          }}>
            <p style={{ margin: 0, padding: '8px 12px 6px',
                         fontSize: 11, color: '#6B7C74', fontWeight: 600,
                         textTransform: 'uppercase',
                         borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              Cambiar a
            </p>
            {menuEstado.opciones.map(op => {
              const c = ESTADO_BADGE[op] || {};
              return (
                <button key={op} onClick={() => aplicarEstado(menuEstado.id, op)}
                  style={{ display: 'block', width: '100%', textAlign: 'left',
                           padding: '9px 14px', border: 'none', background: '#1A1F1D',
                           cursor: 'pointer', fontSize: 13, fontWeight: 600,
                           color: c.color, fontFamily: 'inherit' }}>
                  {ESTADO_LABEL_BTN[op]}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Cabecera */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap',
                    marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar por comunidad..."
            style={{ ...SELECT_ST, width: '100%', boxSizing: 'border-box',
                     paddingRight: busqueda ? 30 : 10, outline: 'none' }} />
          {busqueda && (
            <button onClick={() => setBusqueda('')} title="Limpiar búsqueda"
              style={{ position: 'absolute', right: 6, top: '50%',
                       transform: 'translateY(-50%)', background: 'none',
                       border: 'none', color: '#8A9994', cursor: 'pointer',
                       fontSize: 16, lineHeight: 1, padding: '0 4px',
                       fontFamily: 'inherit' }}>
              ×
            </button>
          )}
        </div>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={SELECT_ST}>
          <option value="">Todos los tipos</option>
          <option value="reparacion">Reparación</option>
          <option value="modernizacion">Modernización</option>
          <option value="normativo">Normativo</option>
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={SELECT_ST}>
          <option value="vigentes">Vigentes (sin aprobados)</option>
          <option value="aceptado">Aprobados (histórico)</option>
          <option value="borrador">Borrador</option>
          <option value="enviado">Enviado</option>
          <option value="rechazado">Rechazado</option>
          <option value="todos">Todos los estados</option>
        </select>
        <button onClick={() => setVista('nuevo')}
          style={{ padding: '8px 18px', background: '#0f6e56', color: 'white',
                   border: 'none', borderRadius: 8, fontWeight: 600,
                   cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>
          + Nuevo presupuesto
        </button>
      </div>

      {error && (
        <div style={{ background: '#2D0F0F', color: '#EF4444', borderRadius: 8,
                      padding: '10px 14px', marginBottom: 14, fontSize: 14,
                      border: '1px solid rgba(198,40,40,0.2)' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#8A9994', textAlign: 'center' }}>Cargando…</p>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.07)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#161C19' }}>
                {['Número', 'Tipo', 'Comunidad', 'Estado', 'Ítems', 'Total', 'Fecha', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left',
                                      fontWeight: 600, color: '#9FAAA5',
                                      borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 20, color: '#6B7C74', textAlign: 'center',
                                             background: '#111714' }}>
                  {busqueda
                    ? `Sin presupuestos para "${busqueda}"`
                    : 'Sin presupuestos'}
                </td></tr>
              )}
              {filtrados.map((p, i) => {
                const badge = ESTADO_BADGE[p.estado] || ESTADO_BADGE.borrador;
                const filaBg = i % 2 === 0 ? '#111714' : '#0D1311';
                const info = itemsCache[p.id];
                return (
                  <React.Fragment key={p.id}>
                  <tr style={{ background: filaBg,
                                           borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '9px 12px' }}>
                      <button onClick={() => toggleExpandir(p.id)} title="Ver ítems"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                 fontFamily: 'monospace', fontWeight: 600, color: '#4CAF50',
                                 fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ color: '#6B7C74', fontSize: 10, width: 8 }}>
                          {expandido === p.id ? '▾' : '▸'}
                        </span>
                        {p.numero}
                      </button>
                      {p.nombre && (
                        <div title={p.nombre}
                          style={{ fontSize: 12, color: '#C9D3CE', marginTop: 3, paddingLeft: 13,
                                   maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.nombre}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#C9D3CE' }}>
                      {TIPO_LABEL[p.tipo] || p.tipo}
                    </td>
                    <td style={{ padding: '9px 12px', maxWidth: 160, overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                 color: '#F4F7F5' }}>
                      {p.comunidad || '—'}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ background: badge.bg, color: badge.color,
                                     borderRadius: 4, padding: '2px 8px',
                                     fontSize: 12, fontWeight: 600 }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'center',
                                 color: '#C9D3CE' }}>{p.n_items}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right',
                                 fontWeight: 600, color: '#F4F7F5' }}>
                      {CLP(p.total)}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#8A9994', whiteSpace: 'nowrap' }}>
                      {new Date(p.created_at).toLocaleDateString('es-CL')}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        <button onClick={() => { setSeleccionado(p.id); setVista('detalle'); }}
                          style={{ padding: '4px 10px', border: '1px solid #2E4A40', borderRadius: 6,
                                   background: '#0D1311', color: '#4CAF50',
                                   cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                          Ver
                        </button>
                        <button onClick={() => descargarPDF(p)}
                          disabled={descargandoId === p.id} title="Descargar PDF"
                          style={{ padding: '4px 10px', border: '1px solid #3A2E5F', borderRadius: 6,
                                   background: '#1A0F2E', color: '#A78BFA',
                                   cursor: descargandoId === p.id ? 'wait' : 'pointer',
                                   fontSize: 12, fontFamily: 'inherit' }}>
                          {descargandoId === p.id ? '…' : 'PDF'}
                        </button>
                        {(p.estado === 'borrador' || (esAdmin && p.estado === 'enviado')) && (
                          <button onClick={() => abrirEditar(p)}
                            style={{ padding: '4px 10px', border: '1px solid #1E3A5F', borderRadius: 6,
                                     background: '#0D1E2E', color: '#60A5FA',
                                     cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                            Editar
                          </button>
                        )}
                        {(TRANSICIONES[p.estado] || []).length > 0 && (
                          <button onClick={e => abrirMenuEstado(e, p.id, p.estado)}
                            style={{ padding: '4px 10px', border: '1px solid #3D2B00', borderRadius: 6,
                                     background: '#1C1200', color: '#F59E0B',
                                     cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                            Estado ▾
                          </button>
                        )}
                        {p.estado === 'borrador' && (
                          <button onClick={() => eliminar(p.id)}
                            style={{ padding: '4px 10px', border: '1px solid rgba(198,40,40,0.2)', borderRadius: 6,
                                     background: '#2D0F0F', color: '#EF4444',
                                     cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandido === p.id && (
                    <tr style={{ background: filaBg }}>
                      <td colSpan={8} style={{ padding: '0 12px 12px 34px' }}>
                        {info?.loading ? (
                          <p style={{ color: '#8A9994', fontSize: 12, margin: '6px 0' }}>Cargando ítems…</p>
                        ) : info?.error ? (
                          <p style={{ color: '#EF4444', fontSize: 12, margin: '6px 0' }}>{info.error}</p>
                        ) : info?.items && info.items.length > 0 ? (
                          <div style={{ borderLeft: '2px solid #2E4A40', paddingLeft: 12 }}>
                            {info.items.map((it, idx) => (
                              <div key={it.id || idx} style={{ display: 'flex', gap: 8,
                                    padding: '3px 0', fontSize: 13, color: '#C9D3CE' }}>
                                <span style={{ color: '#6B7C74', minWidth: 18 }}>{idx + 1}.</span>
                                <span style={{ flex: 1 }}>{it.descripcion}</span>
                                <span style={{ color: '#8A9994', whiteSpace: 'nowrap' }}>× {it.cantidad}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ color: '#6B7C74', fontSize: 12, margin: '6px 0' }}>Sin ítems</p>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
